import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { onboardingAPI } from '@/lib/api';
import { initPush } from '@/lib/push';
import { useActivityTracker } from '@/hooks/useActivityTracker';
import { useTrackReturnState } from '@/hooks/useReturnNavigation';
// 🧠 Bulle "Parler à l'IA" — flottante sur toutes les pages du CRM
// (inclut l'onglet Alertes = observations autonomes Uatu Phase 4a/4b/4c)
import AIChatBubble from '@/components/dashboard/AIChatBubble';
// 📢 Bandeau directives Direction non consultées — sticky sur toutes pages
import DirectiveGlobalBanner from '@/components/layout/DirectiveGlobalBanner';
import { toast } from 'sonner';
import { AlertTriangle, Camera, GraduationCap, Send, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { runBackGuard } from '@/lib/backGuard';

// Bannière Apprenant : progression Académie + bouton « Demander ma validation »
const ApprenantValidationBanner = () => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user?.role !== 'apprenant') return;
        let cancelled = false;
        onboardingAPI.myStatus()
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user?.role]);

    if (user?.role !== 'apprenant') return null;

    const stats = data?.stats || {};
    const status = data?.onboarding_status;
    const alreadyRequested = status === 'formation_terminee';

    const handleRequest = async () => {
        setSubmitting(true);
        try {
            await onboardingAPI.requestValidation();
            setData((prev) => ({ ...(prev || {}), onboarding_status: 'formation_terminee' }));
            toast.success('Demande envoyée ! Un responsable vous contactera pour l\'entretien.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi de la demande');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mb-4 rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-4" data-testid="apprenant-validation-banner">
            <div className="flex items-start gap-3 flex-wrap">
                <GraduationCap className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-[220px]">
                    <p className="font-semibold text-purple-900">Votre parcours de formation</p>
                    <p className="text-sm text-purple-800 mt-0.5">
                        Suivez tous les modules de l'Académie. Une fois terminé, demandez votre validation pour débloquer votre accès Apporteur.
                    </p>
                    {data && (
                        <div className="mt-2">
                            <div className="h-2 w-full max-w-md rounded-full bg-purple-100 overflow-hidden">
                                <div className="h-full bg-purple-600 transition-all" style={{ width: `${stats.progress_pct || 0}%` }} />
                            </div>
                            <p className="text-xs text-purple-700 mt-1">
                                {stats.completed || 0}/{stats.total || 0} modules · {stats.progress_pct || 0}%
                            </p>
                        </div>
                    )}
                </div>
                {alreadyRequested ? (
                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-medium text-emerald-700" data-testid="apprenant-request-sent">
                        <CheckCircle2 className="w-4 h-4" /> Demande envoyée — en attente d'entretien
                    </div>
                ) : (
                    <button
                        onClick={handleRequest}
                        disabled={submitting}
                        data-testid="request-validation-btn"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 transition-colors disabled:opacity-60"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Demander ma validation
                    </button>
                )}
            </div>
        </div>
    );
};

const TrainingRestrictedBanner = () => {
    const { user, isTrainingRestricted } = useAuth();
    if (!isTrainingRestricted()) return null;

    const completed = user?.training_completed_modules;
    const total = user?.training_total_modules;
    const progress = (completed != null && total != null) ? ` (${completed}/${total} modules validés)` : '';

    return (
        <div
            className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
            data-testid="training-restricted-banner"
        >
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
                <p className="font-semibold">Accès au CRM limité</p>
                <p className="mt-0.5">
                    Vous n'êtes pas suffisamment formé pour accéder au CRM{progress}. Terminez vos
                    modules dans l'<span className="font-semibold">Académie</span> pour débloquer
                    automatiquement l'accès complet. En attendant, seuls l'Académie et le Support
                    restent disponibles.
                </p>
            </div>
        </div>
    );
};

// Lot 7 — Rappel non-bloquant : invite à ajouter une photo de profil tant qu'elle
// est absente. Reste affiché en permanence jusqu'à l'ajout (non masquable).
const MissingPhotoBanner = () => {
    const { user } = useAuth();
    if (!user) return null;
    const hasPhoto = !!(user.profile_picture || user.avatar_url);
    if (hasPhoto) return null;

    return (
        <div
            className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-4 flex items-start gap-3"
            data-testid="missing-photo-banner"
        >
            <Camera className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
            <div className="text-sm text-purple-900 flex-1">
                <p className="font-semibold">Ajoutez votre photo de profil</p>
                <p className="mt-0.5">
                    Une photo aide vos collègues et managers à vous identifier sur le CRM
                    (fil d'actualité, fiches, classements). Cela ne prend qu'une minute.
                </p>
            </div>
            <Link
                to="/settings"
                data-testid="missing-photo-banner-cta"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 transition-colors"
            >
                <Camera className="w-4 h-4" /> Ajouter ma photo
            </Link>
        </div>
    );
};

// Bouton Retour global : visible sur toute page atteinte via une navigation interne.
// (location.key === 'default' = entrée directe sans historique interne → pas de retour possible)
const GlobalBackButton = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const HOME_ROUTES = ['/dashboard', '/', '/home'];
    if (location.key === 'default') return null;
    if (HOME_ROUTES.includes(location.pathname)) return null;
    return (
        <button
            type="button"
            data-testid="global-back-btn"
            onClick={() => {
                // Une page peut enregistrer un guard (ex: ProspectDetail non sécurisé)
                // via setBackGuard(). Si ce guard renvoie false, on annule le navigate.
                if (runBackGuard() === false) return;
                navigate(-1);
            }}
            className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900 hover:shadow"
        >
            <ArrowLeft className="h-4 w-4" /> Retour
        </button>
    );
};

export const DashboardLayout = ({ children }) => {
    useActivityTracker();
    useTrackReturnState();
    // Notifications push activées par défaut : on sollicite la permission puis on abonne.
    useEffect(() => {
        const t = setTimeout(() => { initPush({ askIfDefault: true }); }, 2500);
        return () => clearTimeout(t);
    }, []);
    return (
        <div className="min-h-screen bg-slate-50">
            <Sidebar />
            <MobileNav />
            
            {/* Main content */}
            <main 
                className={cn(
                    "transition-all duration-300",
                    "md:ml-64 min-h-screen",
                    "md:pt-20 md:pb-0",
                    "[overflow-x:clip] max-w-full"
                )}
                style={{ 
                    paddingTop: 'calc(3.5rem + env(safe-area-inset-top, 0px))',
                    paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom, 0px))'
                }}
            >
                <div className="p-3 sm:p-4 md:p-6 lg:p-8">
                    <GlobalBackButton />
                    <DirectiveGlobalBanner />
                    <TrainingRestrictedBanner />
                    <ApprenantValidationBanner />
                    <MissingPhotoBanner />
                    {children}
                </div>
            </main>
            {/* 🧠 Bulle IA globale — visible sur toutes les pages du CRM */}
            {/* La liste des observations Uatu est intégrée dans l'onglet "Alertes" du chat */}
            <AIChatBubble />
        </div>
    );
};
